"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const lexer_1 = require("./lexer");
/** Sentinel token for the end of the string */
const EOS = { type: lexer_1.END, value: "" };
class Parser {
    constructor() {
        this.tokens = [];
    }
    parseNumericLiteral() {
        const nextTokenType = this.next.type;
        if (nextTokenType === lexer_1.NumericLiteral) {
            const str = this.chomp().value;
            let value;
            if (str === ".") {
                value = 0;
            }
            else {
                value = Number.parseFloat(str.replace("−", "-"));
            }
            return { type: "NumericLiteral", value };
        }
        return undefined;
    }
    parseGroupExpression() {
        const lParen = this.chompIfType(lexer_1.LPAREN);
        if (lParen == null) {
            return undefined;
        }
        const misplacedRParen = this.chompIfType(lexer_1.RPAREN);
        if (misplacedRParen != null) {
            throw new Error("Unexpected closing parenthesis right after open parenthesis.");
        }
        const child = this.parseAdditiveExpression();
        this.chompIfType(lexer_1.RPAREN);
        return { type: "Group", child };
    }
    parseFunctionOrConstantExpression() {
        const name = this.chompIfType(lexer_1.Alphanumeric);
        if (name == null) {
            return undefined;
        }
        const lParen = this.chompIfType(lexer_1.LPAREN);
        if (lParen) {
            const args = this.parseArgumentList();
            this.chompIfType(lexer_1.RPAREN);
            return { type: "FunctionOrConstant", name, args, asFunction: true };
        }
        else {
            const arg = this.parseAdditiveExpression();
            const args = arg == null ? [] : [arg];
            return { type: "FunctionOrConstant", name, args };
        }
    }
    parseArgumentList() {
        const args = [];
        while (!this.isEmpty) {
            const r = this.parseAdditiveExpression();
            if (r) {
                args.push(r);
            }
            else {
                break;
            }
            if (this.chompIfType(lexer_1.COMMA) == null) {
                break;
            }
        }
        return args;
    }
    parseUnaryNegativeExpression() {
        return this.prefixOperation({
            type: "UnaryNegative",
            operatorTokenTypes: [lexer_1.MINUS],
            descend: () => this.parseAtomicExpression()
        });
    }
    parseAtomicExpression() {
        return (this.parseNumericLiteral() ||
            this.parseGroupExpression() ||
            this.parseFunctionOrConstantExpression());
    }
    parsePowerExpression() {
        return this.infixOperation({
            type: "Power",
            operatorTokenTypes: [lexer_1.POWER],
            descend: () => this.parseUnaryNegativeExpression()
        });
    }
    parseSqrtExpression() {
        return this.prefixOperation({
            type: "Sqrt",
            operatorTokenTypes: [lexer_1.SQRT],
            descend: () => this.parsePowerExpression()
        });
    }
    parseMultiplicativeExpression() {
        return this.infixOperation({
            type: "Multiplicative",
            operatorTokenTypes: [lexer_1.TIMES, lexer_1.DIVIDE],
            descend: () => this.parseSqrtExpression(),
            multiplyConsecutiveOperands: true
        });
    }
    parseAdditiveExpression() {
        return this.infixOperation({
            type: "Additive",
            operatorTokenTypes: [lexer_1.PLUS, lexer_1.MINUS],
            descend: () => this.parseMultiplicativeExpression()
        });
    }
    /**
     * Top-level parse. Parses the input token array.
     * @returns {undefined | MinusOnlyNode | AdditiveNode}
     * `undefined` if the input token array is empty.
     * A `MinusOnly` node if the input only has a `MINUS` token.
     * An `AdditiveNode` if the input has been parsed successfully.
     * @throws {Error} The input has not been parsed successfully.
     */
    parse() {
        if (this.tokens.length === 1 && this.tokens[0].type === lexer_1.MINUS) {
            return { type: "MinusOnly" };
        }
        const result = this.parseAdditiveExpression();
        // Parse top-level minus sign.
        if (!this.isEmpty) {
            throw new Error(`Unexpected token: ${this.next.value} with type: ${this.next.type.name}`);
        }
        return result;
    }
    /**
     * Peek at the next token in the queue.
     * @param tokens
     */
    get next() {
        if (this.isEmpty) {
            return EOS;
        }
        return this.tokens[0];
    }
    /**
     * Chomp the next token in the queue.
     * @param tokens
     */
    chomp() {
        if (this.isEmpty) {
            return EOS;
        }
        return this.tokens.shift();
    }
    chompIfType(...types) {
        if (types.includes(this.next.type)) {
            return this.chomp();
        }
        return undefined;
    }
    get isEmpty() {
        return this.tokens.length === 0;
    }
    /**
     * Helper function for a unary operation
     * with a prefix operator.
     */
    prefixOperation({ type, operatorTokenTypes, descend }) {
        if (this.isEmpty) {
            return undefined;
        }
        // Read an operator.
        const operator = this.chompIfType(...operatorTokenTypes);
        if (operator == null) {
            // Alternate to descend only. (Do not perform operation)
            const operand = descend();
            if (operand != null) {
                return { type, operand };
            }
            else {
                return undefined;
            }
        }
        if (this.isEmpty) {
            // Partial.
            return { type, operator, partial: true };
        }
        // Read an operand.
        const operand = descend();
        if (operand != null) {
            return { type, operator, operand };
        }
        else {
            return undefined;
        }
    }
    /**
     * Helper function for a binary operation
     * with an infix operator.
     */
    infixOperation({ type, operatorTokenTypes, descend, multiplyConsecutiveOperands }) {
        if (this.isEmpty) {
            return undefined;
        }
        const operands = [];
        const operators = [];
        while (!this.isEmpty) {
            // Read an operand.
            const operand = descend();
            // Check if successful parse.
            if (operand == null) {
                break;
            }
            operands.push(operand);
            // Read an operator.
            if (this.isEmpty) {
                break;
            }
            const operator = this.chompIfType(...operatorTokenTypes);
            if (operator != null) {
                operators.push(operator);
                continue;
            }
            if (!multiplyConsecutiveOperands) {
                break;
            }
            if (this.next.type === lexer_1.MINUS) {
                // Expecting additive operation, do not accept.
                break;
            }
            operators.push({ type: lexer_1.TIMES, value: "" });
        }
        if (operands.length === 0) {
            return undefined;
        }
        return {
            type,
            operands,
            operators,
            partial: operands.length === operators.filter(op => op.value).length
        };
    }
}
exports.Parser = Parser;
const staticParser = new Parser();
function parseTokens(tokens) {
    staticParser.tokens = tokens;
    return staticParser.parse();
}
exports.parseTokens = parseTokens;
function parseString(input) {
    return parseTokens(lexer_1.lex(input));
}
exports.parseString = parseString;
//# sourceMappingURL=parser.js.map